"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BlockEditor, TiptapEditor } from "@panda0/panda0-editor";
import {
  Button,
  InfoTooltip,
  SimpleTooltipContent,
  Slider,
  Tooltip,
  useMediaQuery,
} from "@panda0/panda0-ui";
import { cn } from "@panda0/panda0-utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CircleX,
  Edit3,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash,
  Wand2,
  X,
} from "lucide-react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";

import { JobFormData } from "@/lib/types";
import { UpgradeRequiredToast } from "@/ui/shared/upgrade-required-toast";
import { suggestedSkills } from "./constants";
import FullScreenEditor from "./skill/full-screen-editor";
import SubskillField from "./skill/subskill-field";
import { CognitiveSkillData, SkillData, SubskillData } from "./skill/types";
import { JobModalContext } from "./types";

function JobSkill() {
  const { savedJobId, jobData, stateMachine, workspaceId, workspaceNextPlan } =
    useContext(JobModalContext);
  const { isMobile } = useMediaQuery();

  const {
    register,
    control,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<JobFormData>();

  const [skills, setSkills] = useState<SkillData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [manualSkill, setManualSkill] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [editor, setEditor] = useState<TiptapEditor | null>(null);
  const [generatingFromDescription, setGeneratingFromDescription] =
    useState(false);
  const [generatingSkills, setGeneratingSkills] = useState<boolean>(false);
  const [isSkillsLoaded, setIsSkillsLoaded] = useState<boolean>(false);
  const [initialDescription, setInitialDescription] = useState<string>("");
  const [hasQuizGenerated, setHasQuizGenerated] = useState<boolean>(false);
  const [hasSignificantChanges, setHasSignificantChanges] = useState(false);
  const [lastGeneratedContent, setLastGeneratedContent] = useState<string>("");

  const [isEditorExpanded, setIsEditorExpanded] = useState(false);
  const editorRef = useRef<TiptapEditor | null>(null);

  // Determine if description is sufficient to show split view
  const [hasDescription, setHasDescription] = useState(false);
  const [showSkillsPanel, setShowSkillsPanel] = useState(false);

  const cognitiveSkillsData = useMemo(
    () => [
      {
        name: "Analysis",
        description:
          "Breaking down complex information into smaller components to understand patterns and relationships.",
      },
      {
        name: "Evaluation",
        description:
          "Assessing information critically and making judgments based on criteria.",
      },
      {
        name: "Problem Solving",
        description: "Identifying issues and implementing effective solutions.",
      },
      {
        name: "Critical Thinking",
        description:
          "Using logic and reasoning to evaluate arguments and form conclusions.",
      },
      {
        name: "Decision Making",
        description:
          "Selecting the best course of action from available alternatives.",
      },
    ],
    []
  );

  // Check if quiz has been generated
  useEffect(() => {
    if (
      jobData?.pandaQuiz?.questions &&
      jobData.pandaQuiz.questions.length > 0
    ) {
      setHasQuizGenerated(true);
    }
  }, [jobData]);

  // Initialize description from jobData
  useEffect(() => {
    if (jobData?.basic?.description) {
      const description = jobData.basic.description;
      setInitialDescription(description);

      if (typeof description !== "string") {
        console.error("Invalid description type in jobData:", description);
        setInitialDescription("");
        setHasDescription(false);
        setShowSkillsPanel(false);
        return;
      }

      setInitialDescription(description);

      // Check if description is substantial enough to show split view
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = description;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      const hasSubstantialDescription = plainText.length >= 100;
      setHasDescription(hasSubstantialDescription);

      // If we have skills and substantial description, show split view
      if (
        hasSubstantialDescription &&
        (jobData?.skills?.technicalSkills?.length ?? 0) > 0
      ) {
        setShowSkillsPanel(true);
      }
    } else {
      setInitialDescription("");
      setHasDescription(false);
      setShowSkillsPanel(false);
    }
  }, [jobData]);

  // Initialize skills from jobData when available
  useEffect(() => {
    if (jobData?.skills?.technicalSkills?.length ?? 0 > 0) {
      setSkills(jobData?.skills?.technicalSkills ?? []);
      setIsSkillsLoaded(true);

      // If we have skills and substantial description, show split view
      if (hasDescription) {
        setShowSkillsPanel(true);
      }
    }

    // Only load cognitive skills after technical skills are loaded
    if (isSkillsLoaded && (jobData?.skills?.cognitiveSkills?.length ?? 0) > 0) {
      setCognitiveSkills(jobData?.skills?.cognitiveSkills ?? []);
    } else if (
      isSkillsLoaded &&
      skills.length > 0 &&
      cognitiveSkills.length === 0
    ) {
      // Initialize cognitive skills with default values when technical skills exist but no cognitive skills are loaded
      setCognitiveSkills(
        cognitiveSkillsData.map((skill) => ({
          id: skill.name,
          name: skill.name,
          weightage: 20, // Equal distribution
          description: skill.description,
        }))
      );
    }
  }, [
    jobData,
    cognitiveSkillsData,
    isSkillsLoaded,
    skills.length,
    hasDescription,
  ]);

  // Set job description to editor when it becomes available
  useEffect(() => {
    if (editor && initialDescription && !editor.getText()) {
      editor.commands.setContent(initialDescription);
    }
  }, [editor, initialDescription]);

  const [cognitiveSkills, setCognitiveSkills] = useState<CognitiveSkillData[]>(
    []
  );

  const onClickGenerateSkillsFromDescription = async () => {
    await generateSkillsFromDescription();
  };

  const generateSkillsFromDescription = async (newContent?: string) => {
    setGeneratingFromDescription(true);

    // Get content from the appropriate source
    let description: string;

    if (newContent && typeof newContent === "string") {
      // Use the content passed from the editor component
      description = newContent;
    } else if (isEditorExpanded && editorRef.current) {
      // Get from the editor if expanded
      description = editorRef.current.getHTML();
    } else {
      // Use the existing description
      description = initialDescription;
    }

    if (typeof description !== "string") {
      console.error("Invalid description type:", description);
      toast.error("Invalid description format");
      return;
    }

    // Check if description is substantial
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = description;
    const plainText = tempDiv.textContent || tempDiv.innerText || "";

    if (!plainText || plainText.trim().length < 100) {
      toast.error(
        "Please enter a job description with at least 100 characters"
      );
      setGeneratingFromDescription(false);
      return;
    }

    if (!savedJobId) {
      toast.error("Please save the job first to generate skills");
      setGeneratingFromDescription(false);
      return;
    }

    if (hasQuizGenerated) {
      toast.error("Cannot regenerate skills after quiz has been generated");
      setGeneratingFromDescription(false);
      return;
    }

    // Store the content we're generating from
    setLastGeneratedContent(description);

    stateMachine.trackEvent("job_skill_generate_attempt", {
      description_length: description.length,
      has_quiz: hasQuizGenerated,
    });

    try {
      // Save description first
      setValue("basic.description", description, { shouldDirty: true });
      await stateMachine.saveStep("basic", true);

      // Call the API to generate skills
      const res = await fetch(
        `/api/v1/jobs/${savedJobId}/skills?workspaceId=${workspaceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: description,
          }),
        }
      );

      const clonedRes = res.clone();
      if (!res.ok) {
        const { error } = await clonedRes.json();
        stateMachine.trackEvent("job_skill_generation_failed", {
          status_code: clonedRes.status,
          error: error,
        });
        if (error?.message?.includes("Upgrade to")) {
          toast.custom(() => (
            <UpgradeRequiredToast
              title={`Monthly limit reached!`}
              message={error.message}
            />
          ));
          return;
        } else {
          throw new Error("An error occurred");
        }
      }

      const generatedData = await res.json();

      if (generatedData?.skills?.length > 0) {
        stateMachine.trackEvent("job_skill_generation_success", {
          skill_count: generatedData.skills.length,
          has_subskills: generatedData.skills.some(
            (s) => s.subskills?.length > 0
          ),
        });

        generatedData.skills = generatedData.skills.map((x) => ({
          ...x,
          id: x.name,
        }));
        setSkills(generatedData.skills);
        setCognitiveSkills(
          cognitiveSkillsData.map((skill) => ({
            id: skill.name,
            name: skill.name,
            weightage: 20, // Equal distribution
            description: skill.description,
          }))
        );
        setIsSkillsLoaded(true);
        setShowSkillsPanel(true);
        setHasDescription(true);

        // Save the generated skills
        setValue("skills.technicalSkills", generatedData.skills, {
          shouldDirty: true,
        });
        setValue("skills.cognitiveSkills", cognitiveSkills, {
          shouldDirty: true,
        });
        handleCloseEditor();
      } else {
        stateMachine.trackEvent("job_skill_generation_empty", {
          response_data: JSON.stringify(generatedData),
        });
        toast.error("No skills could be generated from the job description");
      }
    } catch (error) {
      stateMachine.trackEvent("job_skill_generation_error", {
        error_message: error instanceof Error ? error.message : "Unknown error",
      });
      console.error("Error generating skills:", error);
      toast.error("Failed to generate skills from description");
    } finally {
      setGeneratingFromDescription(false);
    }
  };

  // Save skills with tracking
  const saveSkills = async () => {
    if (!savedJobId) {
      toast.error("Please save the job first to save skills");
      return;
    }

    stateMachine.trackEvent("job_skill_save_attempt", {
      skill_count: skills.length,
      has_subskills: skills.some(
        (skill) => skill.subskills && skill.subskills.length > 0
      ),
      total_weight: skills.reduce((sum, skill) => sum + skill.weightage, 0),
    });

    try {
      setValue("skills.technicalSkills", skills, { shouldDirty: true });
      const saved = await stateMachine.saveStep("skills", true);

      if (saved) {
        toast.success("Skills saved successfully!");
      }
    } catch (error) {
      console.error("Error saving skills:", error);
      toast.error("Failed to save skills");
    }
  };

  // Save cognitive skills with tracking
  const saveCognitiveSkills = async () => {
    if (!savedJobId) {
      toast.error("Please save the job first to save cognitive skills");
      return;
    }

    stateMachine.trackEvent("job_cognitive_save_attempt", {
      skill_count: cognitiveSkills.length,
      skill_names: cognitiveSkills.map((s) => s.name),
      total_weight: cognitiveSkills.reduce(
        (sum, skill) => sum + skill.weightage,
        0
      ),
    });

    try {
      setValue("skills.cognitiveSkills", cognitiveSkills, {
        shouldDirty: true,
      });
      const saved = await stateMachine.saveStep("skills", true);

      if (saved) {
        toast.success("Cognitive skills saved successfully!");
      }
    } catch (error) {
      console.error("Error saving cognitive skills:", error);
      toast.error("Failed to save cognitive skills");
    }
  };

  const handleSliderChange = useCallback(
    (id: string, newValue: number[]) => {
      stateMachine.trackEvent("job_skill_weightage_change", {
        skill_name: id,
        old_value: skills.find((s) => s.name === id)?.weightage,
        new_value: newValue[0],
      });
      setSkills((prev) => {
        let currentSkill = prev.find((s) => s.name === id);
        if (!currentSkill) return prev;

        let remainingSkills = prev.filter((skill) => skill.name !== id);
        if (remainingSkills.length === 0) return prev;

        let change = newValue[0] - currentSkill.weightage;
        let distributedChange = change / remainingSkills.length;

        let newSkills = prev.map((skill) => {
          if (skill.name === id) {
            return { ...skill, weightage: newValue[0] };
          }
          let adjustedWeight = skill.weightage - distributedChange;
          return {
            ...skill,
            weightage: Math.max(0, Math.min(100, adjustedWeight)),
          };
        });

        // Normalize to 100%
        let normalizedTotal = newSkills.reduce(
          (sum, skill) => sum + skill.weightage,
          0
        );
        let adjustmentFactor = 100 / normalizedTotal;

        return newSkills.map((skill) => ({
          ...skill,
          weightage: Math.round(skill.weightage * adjustmentFactor),
        }));
      });
    },
    [skills, stateMachine]
  );

  const handleDeleteSkill = useCallback(
    (name: string) => {
      // Don't allow skill deletion if quiz has been generated
      if (hasQuizGenerated) {
        toast.error("Cannot modify skills after quiz has been generated");
        return;
      }

      stateMachine.trackEvent("job_skill_delete", {
        skill_name: name,
        has_quiz: hasQuizGenerated,
      });

      setSkills((prev) => {
        const filtered = prev.filter((skill) => skill.name !== name);

        // If no skills remain, hide skills panel
        if (filtered.length === 0) {
          setShowSkillsPanel(false);
        }

        // Redistribute weights if any skills remain
        if (filtered.length > 0) {
          const totalWeight = filtered.reduce(
            (sum, skill) => sum + skill.weightage,
            0
          );
          if (totalWeight < 100) {
            const adjustmentFactor = 100 / totalWeight;
            return filtered.map((skill) => ({
              ...skill,
              weightage: Math.round(skill.weightage * adjustmentFactor),
            }));
          }
        }

        return filtered;
      });
    },
    [hasQuizGenerated, stateMachine]
  );

  const handleSubskillChange = useCallback(
    (name: string, updatedSubskills: SubskillData[]) => {
      // Don't allow subskill changes if quiz has been generated
      if (hasQuizGenerated) {
        toast.error("Cannot modify skills after quiz has been generated");
        return;
      }

      setSkills((prevSkills) =>
        prevSkills.map((skill) =>
          skill.name === name
            ? { ...skill, subskills: updatedSubskills }
            : skill
        )
      );
    },
    [hasQuizGenerated]
  );

  const handleAddSkill = useCallback(() => {
    // Don't allow adding skills if quiz has been generated
    if (hasQuizGenerated) {
      toast.error("Cannot add skills after quiz has been generated");
      setIsModalOpen(false);
      return;
    }

    if (manualSkill.trim() === "" && selectedSkills.length === 0) return;

    const combinedSkills = [...selectedSkills, manualSkill.trim()].filter(
      Boolean
    );

    const newSkillObj: SkillData = {
      id: Date.now().toString(),
      name: combinedSkills.join(", "),
      weightage: 10, // Default weight
      subskills: [],
    };

    setSkills((prevSkills) => {
      const newSkills = [...prevSkills, newSkillObj];

      // Redistribute weights
      const totalWeight = newSkills.reduce(
        (sum, skill) => sum + skill.weightage,
        0
      );
      const adjustmentFactor = 100 / totalWeight;

      return newSkills.map((skill) => ({
        ...skill,
        weightage: Math.round(skill.weightage * adjustmentFactor),
      }));
    });

    // Show the skills panel when we add a skill
    setShowSkillsPanel(true);

    setIsModalOpen(false);
    setSelectedSkills([]);
    setManualSkill("");
  }, [selectedSkills, manualSkill, hasQuizGenerated]);

  const handleCognitiveSliderChange = useCallback(
    (name: string, newValue: number[]) => {
      // Don't allow changing cognitive skills if quiz has been generated
      if (hasQuizGenerated) {
        toast.error(
          "Cannot modify cognitive skills after quiz has been generated"
        );
        return;
      }

      setCognitiveSkills((prev) => {
        let currentSkill = prev.find((s) => s.name === name);
        if (!currentSkill) return prev;

        let remainingSkills = prev.filter((skill) => skill.name !== name);
        if (remainingSkills.length === 0) return prev;

        let change = newValue[0] - currentSkill.weightage;
        let distributedChange = change / remainingSkills.length;

        let newSkills = prev.map((skill) => {
          if (skill.name === name) {
            return { ...skill, weightage: newValue[0] };
          }
          let adjustedWeight = skill.weightage - distributedChange;
          return {
            ...skill,
            weightage: Math.max(0, Math.min(100, adjustedWeight)),
          };
        });

        // Normalize to 100%
        let normalizedTotal = newSkills.reduce(
          (sum, skill) => sum + skill.weightage,
          0
        );
        let adjustmentFactor = 100 / normalizedTotal;

        return newSkills.map((skill) => ({
          ...skill,
          weightage: Math.round(skill.weightage * adjustmentFactor),
        }));
      });
    },
    [hasQuizGenerated]
  );

  const handleCloseEditor = useCallback(() => {
    // Save the current content
    if (editorRef.current) {
      const currentContent = editorRef.current.getHTML();
      if (currentContent && currentContent !== "<p></p>") {
        setInitialDescription(currentContent);
      }

      // Check if description is substantial
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = currentContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      const hasSubstantialDescription = plainText.length > 10;
      setHasDescription(hasSubstantialDescription);

      // Only show skills panel if we have skills and a description
      if (hasSubstantialDescription && skills.length > 0) {
        setShowSkillsPanel(true);
      }
    }

    // Close the editor
    setIsEditorExpanded(false);
    if (!hasDescription)
      setHasDescription(true);
  }, [skills.length]);

  // Enter editor mode
  const handleEditDescription = useCallback(() => {
    // Don't allow editing if quiz has been generated
    if (hasQuizGenerated) {
      toast.error("Cannot edit description after quiz has been generated");
      return;
    }

    setIsEditorExpanded(true);
  }, [hasQuizGenerated]);

  const checkEditorContent = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.getHTML();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      // Check for minimum content length
      const hasEnoughContent = plainText.trim().length >= 100;

      // Check if content has changed significantly from last generated content
      if (lastGeneratedContent && typeof lastGeneratedContent === "string") {
        const tempLastDiv = document.createElement("div");
        tempLastDiv.innerHTML = lastGeneratedContent;
        const lastText = tempLastDiv.textContent || tempLastDiv.innerText || "";

        // Calculate how different the content is from last time (if there's last content)
        if (lastText.trim().length > 0) {
          const contentChangePercent =
            Math.abs(plainText.length - lastText.length) / lastText.length;
          const hasChanged = contentChangePercent > 0.15;

          setHasSignificantChanges(hasChanged && hasEnoughContent);
          return hasEnoughContent;
        }
      }

      // If no previous content or invalid previous content
      setHasSignificantChanges(true);
      return hasEnoughContent;
    }
    return false;
  }, [editorRef, lastGeneratedContent]);

  return (
    <div className="w-full h-full">
      <AnimatePresence mode="wait">
        {isEditorExpanded || !hasDescription ? (
          // Show bounded editor when expanded OR when no description exists
          <motion.div
            className="w-full h-full"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FullScreenEditor
              editor={editor}
              setEditor={setEditor}
              initialDescription={initialDescription}
              onClose={handleCloseEditor}
              onGenerate={generateSkillsFromDescription}
              isGenerating={generatingFromDescription}
              hasQuizGenerated={hasQuizGenerated}
              savedJobId={savedJobId}
              workspaceId={workspaceId}
              editorRef={editorRef}
              isNew={!hasDescription}
              lastGeneratedContent={lastGeneratedContent}
            />
          </motion.div>
        ) : (
          // Split View for when we have content
          <motion.div
            className="flex w-full h-full overflow-hidden"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Description Panel */}
            <motion.div
              className={cn(
                "h-full flex-shrink-0 transition-all duration-500 ease-in-out",
                showSkillsPanel ? (isMobile ? "w-full" : "w-1/3") : "w-full"
              )}
              layout
              initial={{
                width: showSkillsPanel
                  ? isMobile
                    ? "100%"
                    : "33.333%"
                  : "100%",
              }}
              animate={{
                width: showSkillsPanel
                  ? isMobile
                    ? "100%"
                    : "33.333%"
                  : "100%",
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="h-full flex flex-col bg-white rounded-lg border border-neutral-200 shadow-sm">
                {/* Header */}
                <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 flex justify-between items-center">
                  <h2 className="text-lg font-medium flex items-center gap-2">
                    Job Description
                    <InfoTooltip
                      content={
                        <SimpleTooltipContent
                          title="Enter a detailed job description to automatically generate skill requirements."
                          cta="Learn more about AI-powered skill extraction."
                          href="#"
                        />
                      }
                    />
                  </h2>

                  <div className="flex gap-2">
                    {!isEditorExpanded && (
                      <Button
                        variant="outline"
                        onClick={handleEditDescription}
                        icon={<Edit3 className="w-4 h-4" />}
                        text={isMobile ? undefined : "Edit"}
                        className="h-9 w-fit"
                        disabled={
                          hasQuizGenerated ||
                          generatingFromDescription ||
                          generatingSkills
                        }
                        disabledTooltip={
                          hasQuizGenerated
                            ? "Cannot edit description after quiz has been generated"
                            : generatingFromDescription || generatingSkills
                              ? "Cannot edit while generating skills"
                              : undefined
                        }
                      />
                    )}
                    {isEditorExpanded ? (
                      <>
                        <Button
                          variant="primary"
                          onClick={onClickGenerateSkillsFromDescription}
                          text={isMobile ? undefined : "Generate Skills"}
                          icon={<Wand2 className="w-4 h-4" />}
                          loading={generatingFromDescription}
                          disabled={
                            generatingFromDescription ||
                            generatingSkills ||
                            !savedJobId ||
                            !hasDescription ||
                            hasQuizGenerated
                          }
                          className="h-9 w-fit"
                          disabledTooltip={
                            hasQuizGenerated
                              ? "Cannot regenerate skills after quiz has been generated"
                              : !editorRef.current?.getText().length
                                ? "Please add a job description first"
                                : undefined
                          }
                        />
                        <Button
                          variant="secondary"
                          onClick={handleCloseEditor}
                          text="Done"
                          icon={<X className="w-4 h-4" />}
                          className="h-9 w-fit"
                        />
                      </>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={onClickGenerateSkillsFromDescription}
                        text={isMobile ? undefined : "Generate Skills"}
                        icon={<Wand2 className="w-4 h-4" />}
                        loading={generatingFromDescription}
                        disabled={
                          generatingFromDescription ||
                          generatingSkills ||
                          !savedJobId ||
                          !hasDescription ||
                          hasQuizGenerated
                        }
                        className="h-9 w-fit"
                        disabledTooltip={
                          hasQuizGenerated
                            ? "Cannot regenerate skills after quiz has been generated"
                            : !hasDescription
                              ? "Please add a job description first"
                              : undefined
                        }
                      />
                    )}
                  </div>
                </div>

                {/* Document Viewer/Editor */}
                <div className="flex-1 relative overflow-auto">
                  {/* PDF-like styling */}
                  <div className="min-h-full p-6 bg-neutral-50">
                    <motion.div
                      className="bg-white border border-neutral-200 shadow-sm rounded-lg p-8 m-auto max-w-4xl prose prose-neutral prose-headings:text-neutral-800 prose-p:text-neutral-700"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      {/* Page styling */}
                      <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-neutral-100 to-transparent" />

                      {/* Content - Toggle between editor and display */}
                      {isEditorExpanded ? (
                        <div className="pb-8">
                          <BlockEditor
                            upgradeElement={
                              <UpgradeRequiredToast
                                title="You've exceeded your AI usage limit"
                                message="Error generating response"
                              />
                            }
                            placeholder="Paste job description or use ~ to open AI editor"
                            onCreate={(editor) => {
                              setEditor(editor);
                              editorRef.current = editor;

                              // Set initial content if available
                              if (
                                initialDescription &&
                                typeof initialDescription === "string" &&
                                initialDescription.trim() !== ""
                              ) {
                                editor.commands.setContent(initialDescription);
                              }

                              // Initial check after creation
                              setTimeout(checkEditorContent, 100);
                            }}
                            onUpdate={(editor) => {
                              setEditor(editor);
                              editorRef.current = editor;
                              setTimeout(checkEditorContent, 100);
                            }}
                            api={`/api/v1/ai/completion?workspaceId=${workspaceId}`}
                            appendToSelector=".modal-form"
                            content={
                              initialDescription &&
                                typeof initialDescription === "string"
                                ? initialDescription
                                : ""
                            }
                            key="editor-instance"
                          />
                        </div>
                      ) : hasDescription ? (
                        <div
                          className="pb-8"
                          dangerouslySetInnerHTML={{
                            __html: initialDescription,
                          }}
                        />
                      ) : (
                        <div className="py-12 flex flex-col items-center justify-center text-center text-neutral-500">
                          <FileText className="h-12 w-12 mb-4 text-neutral-300" />
                          <h3 className="text-xl font-medium text-neutral-600 mb-2">
                            No Job Description
                          </h3>
                          <p className="max-w-md text-neutral-500">
                            Add a job description to get started. This will help
                            us generate skills required for the position.
                          </p>
                          <Button
                            variant="outline"
                            onClick={handleEditDescription}
                            text="Add Description"
                            icon={<Edit3 className="w-4 h-4" />}
                            className="mt-6"
                          />
                        </div>
                      )}

                      {/* Page number and footer styling */}
                      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-neutral-100 to-transparent" />
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Skills Panel */}
            <AnimatePresence mode="sync">
              {showSkillsPanel && !isMobile && (
                <motion.div
                  className="h-full w-2/3 flex-1 pl-6 overflow-auto"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 30 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <div className="h-full flex flex-col space-y-6 overflow-auto">
                    {/* Technical Skills Section */}
                    <motion.div
                      className={cn(
                        "rounded-xl border border-neutral-200 bg-white shadow-sm",
                        (generatingSkills || generatingFromDescription) &&
                        "opacity-75"
                      )}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 flex justify-between items-center">
                        <h2 className="text-lg font-medium flex items-center gap-2">
                          Technical Skills
                          <InfoTooltip
                            content={
                              <SimpleTooltipContent
                                title="Define the technical skills required for this position and their relative importance."
                                cta="Learn more about optimizing skill weightage."
                                href="#"
                              />
                            }
                          />
                        </h2>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="primary"
                            onClick={saveSkills}
                            text={isMobile ? undefined : "Save"}
                            icon={<Save className="w-4 h-4" />}
                            disabled={
                              generatingSkills ||
                              generatingFromDescription ||
                              !savedJobId ||
                              skills.length === 0
                            }
                            className="h-9 w-fit"
                          />
                          <Button
                            variant="primary"
                            onClick={() => setIsModalOpen(true)}
                            text={isMobile ? undefined : "Add Skill"}
                            icon={<Plus className="w-4 h-4" />}
                            disabled={
                              generatingSkills ||
                              generatingFromDescription ||
                              hasQuizGenerated
                            }
                            className="h-9 w-fit"
                            disabledTooltip={
                              hasQuizGenerated
                                ? "Cannot add skills after quiz has been generated"
                                : undefined
                            }
                          />
                        </div>
                      </div>

                      {hasQuizGenerated && (
                        <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                          <AlertCircle className="text-amber-500 h-4 w-4 flex-shrink-0" />
                          <p className="text-sm text-amber-700">
                            Skills are locked because a quiz has been generated.
                            Modifying skills would invalidate the quiz. Weights
                            can still be modified to configure the quiz
                            complexity
                          </p>
                        </div>
                      )}

                      {skills.length === 0 ? (
                        <div className="p-8 flex flex-col items-center justify-center text-center text-neutral-500">
                          {generatingSkills || generatingFromDescription ? (
                            <>
                              <Loader2 className="w-8 h-8 mb-3 animate-spin" />
                              <p className="text-sm max-w-md">
                                {generatingFromDescription
                                  ? "Analyzing job description and extracting skills..."
                                  : "Generating skills..."}
                              </p>
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-8 h-8 mb-3" />
                              <p className="text-sm max-w-md">
                                No skills have been defined yet. Add skills
                                manually or generate them automatically from the
                                job description.
                              </p>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="p-4">
                          <div className="mb-4 grid text-sm font-medium text-neutral-500 px-3 grid-cols-12">
                            <div className="col-span-3">Skill</div>
                            <div className="col-span-1 text-center">
                              Ratio %
                            </div>
                            <div className="col-span-7 text-center">
                              Sub-skills
                            </div>
                            <div className="col-span-1"></div>
                          </div>

                          <motion.div
                            className="space-y-3"
                            layout
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ staggerChildren: 0.05 }}
                          >
                            {skills.map((skill, index) => (
                              <motion.div
                                key={skill.name}
                                className="grid items-center bg-white rounded-lg border border-neutral-200 p-3 gap-3 hover:shadow-sm transition-shadow grid-cols-12"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                  duration: 0.3,
                                  delay: index * 0.05,
                                }}
                                layout
                              >
                                <div className="col-span-3">
                                  <div className="font-medium text-neutral-800">
                                    {skill.name}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 col-span-3">
                                  <Slider
                                    value={[skill.weightage]}
                                    max={100}
                                    step={1}
                                    className="flex-1"
                                    onValueChange={(value) =>
                                      handleSliderChange(skill.name, value)
                                    }
                                  />
                                  <span className="text-sm font-medium text-neutral-600 w-9 text-right">
                                    {skill.weightage}%
                                  </span>
                                </div>

                                <div className="col-span-5">
                                  <SubskillField
                                    skill={skill}
                                    onSubskillChange={handleSubskillChange}
                                  />
                                </div>

                                <div className="col-span-1 text-right">
                                  <Tooltip
                                    content={
                                      hasQuizGenerated
                                        ? "Cannot remove skills after quiz has been generated"
                                        : "Remove skill"
                                    }
                                  >
                                    <button
                                      onClick={() =>
                                        handleDeleteSkill(skill.name)
                                      }
                                      className={cn(
                                        "p-1.5 rounded-md transition-colors",
                                        hasQuizGenerated
                                          ? "text-neutral-300 cursor-not-allowed"
                                          : "text-neutral-400 hover:text-red-500 hover:bg-neutral-100"
                                      )}
                                      aria-label="Delete skill"
                                      disabled={hasQuizGenerated}
                                    >
                                      <Trash className="w-4 h-4" />
                                    </button>
                                  </Tooltip>
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        </div>
                      )}
                    </motion.div>

                    {/* Cognitive Skills Section - Only show after technical skills are populated */}
                    {isSkillsLoaded && skills.length > 0 && (
                      <motion.div
                        className="rounded-xl border border-neutral-200 bg-white shadow-sm"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                      >
                        <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 flex justify-between items-center">
                          <h2 className="text-lg font-medium flex items-center gap-2">
                            Cognitive Skills
                            <InfoTooltip
                              content={
                                <SimpleTooltipContent
                                  title="Define the cognitive abilities required for success in this role."
                                  cta="Learn more about cognitive skills quiz."
                                  href="#"
                                />
                              }
                            />
                          </h2>
                          <Button
                            variant="primary"
                            onClick={saveCognitiveSkills}
                            text={isMobile ? undefined : "Save"}
                            icon={<Save className="w-4 h-4" />}
                            disabled={
                              generatingSkills ||
                              generatingFromDescription ||
                              !savedJobId ||
                              cognitiveSkills.length === 0
                            }
                            className="h-9 w-fit"
                          />
                        </div>

                        {hasQuizGenerated && (
                          <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                            <AlertCircle className="text-amber-500 h-4 w-4 flex-shrink-0" />
                            <p className="text-sm text-amber-700">
                              Cognitive skills are locked because a quiz has
                              been generated. Modifying skills would invalidate
                              the quiz. Weights can still be modified to
                              configure the quiz complexity
                            </p>
                          </div>
                        )}

                        <div className="p-4">
                          <div className="mb-4 grid text-sm font-medium text-neutral-500 px-3 grid-cols-12">
                            <div className="col-span-4">Skill</div>
                            <div className="col-span-8 text-center">
                              Importance
                            </div>
                          </div>

                          <motion.div
                            className="space-y-3"
                            layout
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ staggerChildren: 0.05 }}
                          >
                            {cognitiveSkills.map((skill, index) => (
                              <motion.div
                                key={skill.name}
                                className="grid items-center bg-white rounded-lg border border-neutral-200 p-3 gap-3 hover:shadow-sm transition-shadow grid-cols-12"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                  duration: 0.3,
                                  delay: 0.1 + index * 0.05,
                                }}
                              >
                                <div className="col-span-4">
                                  <div className="font-medium text-neutral-800">
                                    {skill.name}
                                  </div>
                                  <div className="text-xs text-neutral-500 mt-1">
                                    {skill.description}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 col-span-8">
                                  <Slider
                                    value={[skill.weightage]}
                                    max={100}
                                    step={1}
                                    className="flex-1"
                                    onValueChange={(value) =>
                                      handleCognitiveSliderChange(
                                        skill.name,
                                        value
                                      )
                                    }
                                    disabled={hasQuizGenerated}
                                  />
                                  <span className="text-sm text-neutral-600 w-9 text-right">
                                    {skill.weightage}%
                                  </span>
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mobile Skills Panel (shown below description when in mobile view) */}
            {showSkillsPanel && isMobile && (
              <motion.div
                className="w-full mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {/* Technical Skills Section */}
                <div className="mb-6 rounded-xl border border-neutral-200 bg-white">
                  <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 flex justify-between items-center">
                    <h2 className="text-lg font-medium">Technical Skills</h2>
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        onClick={saveSkills}
                        icon={<Save className="w-4 h-4" />}
                        className="h-9 w-fit"
                        disabled={
                          generatingSkills ||
                          generatingFromDescription ||
                          !savedJobId ||
                          skills.length === 0
                        }
                      />
                      <Button
                        variant="primary"
                        onClick={() => setIsModalOpen(true)}
                        icon={<Plus className="w-4 h-4" />}
                        className="h-9 w-fit"
                        disabled={
                          generatingSkills ||
                          generatingFromDescription ||
                          hasQuizGenerated
                        }
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal for Adding New Skill - outside the layout */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-6 rounded-xl shadow-lg w-[500px] max-w-[90vw] m-4"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
            >
              <h2 className="text-lg font-semibold mb-4">
                Add Technical Skill
              </h2>

              {hasQuizGenerated && (
                <div className="mb-4 p-3 bg-amber-50 rounded-md border border-amber-200 text-amber-800 text-sm flex items-start gap-2">
                  <AlertCircle className="text-amber-500 h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Cannot add new skills after quiz has been generated. Adding
                    new skills would invalidate the generated quiz questions.
                  </div>
                </div>
              )}

              <input
                type="text"
                placeholder="Enter skill name"
                value={manualSkill}
                onChange={(e) => setManualSkill(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    manualSkill.trim() !== "" &&
                    selectedSkills.length < 2 &&
                    !hasQuizGenerated
                  ) {
                    setSelectedSkills([...selectedSkills, manualSkill.trim()]);
                    setManualSkill("");
                  }
                }}
                disabled={selectedSkills.length >= 2 || hasQuizGenerated}
                className={cn(
                  "border border-neutral-300 rounded-md p-2 focus:border-neutral-500 focus:ring-neutral-500 w-full",
                  (selectedSkills.length >= 2 || hasQuizGenerated) &&
                  "opacity-50 cursor-not-allowed"
                )}
              />

              <h3 className="font-medium text-neutral-700 mt-4 mb-2">
                Suggested Skills
              </h3>

              <div className="flex flex-wrap gap-2 mb-4">
                {suggestedSkills.map((skill, index) => (
                  <motion.div
                    key={index}
                    className={cn(
                      "flex items-center px-3 py-1 rounded-full transition-colors border",
                      selectedSkills.includes(skill)
                        ? "bg-neutral-100 border-neutral-300"
                        : "border-neutral-200 hover:bg-neutral-50",
                      (selectedSkills.length >= 2 &&
                        !selectedSkills.includes(skill)) ||
                        hasQuizGenerated
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    )}
                    onClick={() => {
                      if (hasQuizGenerated) return;

                      if (selectedSkills.includes(skill)) {
                        setSelectedSkills(
                          selectedSkills.filter((s) => s !== skill)
                        );
                      } else if (selectedSkills.length < 2) {
                        setSelectedSkills([...selectedSkills, skill]);
                      }
                    }}
                    whileHover={
                      !(
                        selectedSkills.length >= 2 &&
                        !selectedSkills.includes(skill)
                      ) && !hasQuizGenerated
                        ? { scale: 1.05 }
                        : {}
                    }
                    whileTap={
                      !(
                        selectedSkills.length >= 2 &&
                        !selectedSkills.includes(skill)
                      ) && !hasQuizGenerated
                        ? { scale: 0.95 }
                        : {}
                    }
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                  >
                    <span className="text-sm">{skill}</span>
                    {selectedSkills.includes(skill) && (
                      <CircleX className="ml-1 w-4 h-4" />
                    )}
                  </motion.div>
                ))}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="secondary"
                  text="Cancel"
                  className="h-9 w-fit"
                  onClick={() => setIsModalOpen(false)}
                />
                <Button
                  variant="primary"
                  text="Add Skill"
                  className="h-9 w-fit"
                  onClick={handleAddSkill}
                  disabled={
                    (selectedSkills.length === 0 && !manualSkill.trim()) ||
                    hasQuizGenerated
                  }
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default React.memo(JobSkill);
